import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, XCircle, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/common/Logo";
import { Money } from "@/components/common/Money";
import { useTransaction } from "@/hooks/useTransactions";
import { useQueryClient } from "@tanstack/react-query";
import { confirmPaydunyaToken } from "@/lib/payments";

export function PaymentReturn() {
  const { t } = useTranslation();
  const { txId: txIdParam } = useParams();
  const [params] = useSearchParams();
  // Nouveau format : /payment/return/:txId. Ancien format (avant le fix
  // du bug return_url) : /payment/return?tx=...
  const txId = txIdParam ?? params.get("tx") ?? undefined;
  const { data: tx, isLoading, refetch } = useTransaction(txId);
  const qc = useQueryClient();
  const [attempts, setAttempts] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const triggered = useRef<Set<string>>(new Set());

  // À chaque fois qu'on a un token et que la tx est encore pending,
  // on déclenche paydunya-confirm pour forcer la mise à jour.
  // Polling : 6 tentatives à 3s d'intervalle (max ~18s).
  useEffect(() => {
    if (!tx || !tx.paydunya_token) return;
    if (tx.status !== "pending") return;
    if (attempts >= 6) return;
    if (triggered.current.has(`${tx.paydunya_token}-${attempts}`)) return;

    triggered.current.add(`${tx.paydunya_token}-${attempts}`);
    setConfirming(true);

    const timer = setTimeout(async () => {
      try {
        await confirmPaydunyaToken(tx.paydunya_token!);
      } catch {
        // silencieux : on retentera si toujours pending
      }
      await refetch();
      qc.invalidateQueries({ queryKey: ["profile"] });
      qc.invalidateQueries({ queryKey: ["transactions"] });
      setAttempts((a) => a + 1);
      setConfirming(false);
    }, attempts === 0 ? 500 : 3000);

    return () => clearTimeout(timer);
  }, [tx, attempts, refetch, qc]);

  // Quand on bascule en success, on invalide partout pour refresh le solde
  useEffect(() => {
    if (tx?.status === "success" || tx?.status === "failed") {
      qc.invalidateQueries({ queryKey: ["profile"] });
      qc.invalidateQueries({ queryKey: ["transactions"] });
    }
  }, [tx?.status, qc]);

  async function manualRetry() {
    if (!tx?.paydunya_token) return;
    setConfirming(true);
    try {
      await confirmPaydunyaToken(tx.paydunya_token);
    } catch {
      // ignored
    }
    await refetch();
    qc.invalidateQueries({ queryKey: ["profile"] });
    qc.invalidateQueries({ queryKey: ["transactions"] });
    setConfirming(false);
  }

  const stillPending = !!tx && tx.status === "pending";
  const exhausted = attempts >= 6 && stillPending;
  const state =
    !tx || isLoading
      ? "checking"
      : tx.status === "success"
        ? "success"
        : tx.status === "failed"
          ? "failed"
          : exhausted
            ? "pending-long"
            : "checking";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12 text-center">
      <Logo className="mb-8" />

      {state === "checking" && (
        <>
          <Loader2 className="size-16 text-primary animate-spin mb-4" />
          <h1 className="text-2xl font-bold">{t("wallet.paymentReturn.checking")}</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Vérification auprès de PayDunya · tentative {Math.min(attempts + 1, 6)}/6
          </p>
        </>
      )}

      {state === "success" && tx && (
        <>
          <CheckCircle2 className="size-20 text-success mb-4" />
          <h1 className="text-2xl font-bold">{t("wallet.paymentReturn.success")}</h1>
          <p className="text-muted-foreground mt-2">
            Wallet crédité de <Money amount={tx.amount} className="font-semibold text-foreground" />
          </p>
          <Button asChild className="mt-6"><Link to="/wallet">{t("wallet.paymentReturn.backToWallet")}</Link></Button>
        </>
      )}

      {state === "failed" && (
        <>
          <XCircle className="size-20 text-destructive mb-4" />
          <h1 className="text-2xl font-bold">{t("wallet.paymentReturn.failed")}</h1>
          <p className="text-muted-foreground mt-2">{t("wallet.paymentReturn.failedHint")}</p>
          <Button asChild className="mt-6" variant="outline"><Link to="/wallet">{t("wallet.paymentReturn.backToWallet")}</Link></Button>
        </>
      )}

      {state === "pending-long" && (
        <>
          <Loader2 className="size-16 text-warning mb-4" />
          <h1 className="text-2xl font-bold">Paiement en cours de validation</h1>
          <p className="text-muted-foreground mt-2 max-w-sm">
            Ton paiement est encore en cours de traitement chez PayDunya. Le solde
            sera mis à jour dès que la transaction sera confirmée.
          </p>
          <div className="mt-6 flex gap-2">
            <Button onClick={manualRetry} loading={confirming} variant="outline">
              <RefreshCw className="size-4" /> Vérifier maintenant
            </Button>
            <Button asChild>
              <Link to="/wallet">{t("wallet.paymentReturn.backToWallet")}</Link>
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
